// Soporte para cuando el formulario va dentro de un iframe en otra web.
//
// Hay dos cosas que la página de fuera no puede averiguar por su cuenta:
// cuánto mide el formulario (es multipaso y cada paso mide distinto) y cuándo
// el visitante ha pasado al siguiente. Se las contamos por postMessage y ella
// ajusta el alto del iframe — así no hay ni recuadro con scroll propio ni
// hueco vacío debajo.
//
// El fragmento que hay que pegar en la web está en el README.
import { useEffect, useRef } from 'react';

export const MENSAJE_ALTURA = 'latiendahome:altura';
export const MENSAJE_ARRIBA = 'latiendahome:ir-arriba';

/** ¿Nos han metido en un iframe? */
export function estaEmbebido() {
  return window.self !== window.top;
}

// Los mensajes van a cualquier origen ('*') porque no sabemos en qué web nos
// han embebido. No es un problema: lo único que sale de aquí es un número de
// píxeles. Quien escucha sí debe comprobar el origen, y el fragmento del README
// lo hace.
function avisar(mensaje) {
  if (!estaEmbebido()) return;
  window.parent.postMessage(mensaje, '*');
}

/**
 * Mide el nodo y le va contando su alto a la página de fuera.
 *
 * Se mide el elemento, no `document.documentElement.scrollHeight`: ese nunca
 * baja del alto del iframe, así que el marco crecería con cada paso largo y no
 * volvería a encoger.
 */
export function useAvisarAltura(ref) {
  useEffect(() => {
    const nodo = ref.current;
    if (!estaEmbebido() || !nodo) return undefined;

    let ultimo = 0;
    const medir = () => {
      const alto = Math.ceil(nodo.getBoundingClientRect().height);
      // Sin este corte, un redondeo de medio píxel rebota entre los dos marcos.
      if (alto === ultimo) return;
      ultimo = alto;
      avisar({ tipo: MENSAJE_ALTURA, alto });
    };

    const observador = new ResizeObserver(medir);
    observador.observe(nodo);
    medir();
    return () => observador.disconnect();
  }, [ref]);
}

/**
 * Pide a la web de fuera que suba al principio del formulario. Al cambiar de
 * paso el iframe se redibuja entero, pero el scroll es de la página de fuera:
 * sin esto, quien pulsa "Siguiente" desde abajo se queda mirando el pie.
 * No se avisa en el primer render, que sería dar un tirón nada más cargar.
 */
export function useAvisarPaso(paso) {
  const primero = useRef(true);
  useEffect(() => {
    if (primero.current) {
      primero.current = false;
      return;
    }
    avisar({ tipo: MENSAJE_ARRIBA });
  }, [paso]);
}
